import { useState, useEffect } from 'react';
import { apiClient } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AppPageIntro from '@/components/AppPageIntro';
import { Progress } from '@/components/ui/progress';
import {
  Heart,
  Utensils,
  Dumbbell,
  TrendingUp,
  TrendingDown,
  Lightbulb,
  Sparkles
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

export default function HealthMoney() {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fastFoodSpending = Number(healthData?.fast_food_spending || 0);
  const sportsSpending = Number(healthData?.sports_spending || 0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await apiClient.get('/health/analysis');
      setHealthData(response.data);
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="h-64 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const chartData = [
    { name: 'Uit eten / Fast food', value: fastFoodSpending, color: '#f97316' },
    { name: 'Sport & Fitness', value: sportsSpending, color: '#22c55e' },
  ];

  const total = fastFoodSpending + sportsSpending || 1;
  const fastFoodRatio = ((fastFoodSpending / total) * 100).toFixed(0);
  const sportsRatio = ((sportsSpending / total) * 100).toFixed(0);
  const hasData = fastFoodSpending > 0 || sportsSpending > 0;

  return (
    <div className="space-y-6" data-testid="health-money-page">
      <AppPageIntro
        eyebrow="Gewoontes in beeld"
        title="Gezondheid vs Geld"
        description="Krijg een simpel beeld van wat je uitgeeft aan eten buiten de deur en aan sport, in dezelfde app-stijl als de rest van je overzicht."
        badges={['Spiegel', 'Patronen', 'Welzijn']}
      />

      {!hasData && (
        <Card className="shadow-card border-primary/15 bg-primary/5">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Begin met je gezondheidsoverzicht
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 lg:grid-cols-3">
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">1. Importeer je transacties</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Deze pagina werkt pas goed als je uitgaven al in de app staan.
              </p>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">2. Bekijk je gewoontes</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Je ziet hier later sneller hoeveel naar eten buiten de deur of sport gaat.
              </p>
            </div>
            <div className="rounded-xl border bg-background p-4">
              <p className="font-medium">3. Gebruik het als spiegel</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Dit onderdeel helpt vooral om patronen te zien, niet om streng te oordelen.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Nudge Alert */}
      {healthData?.nudge_message && (
        <Card className="shadow-card border-orange-200 bg-orange-50">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-orange-800">Bespaartip</h3>
                <p className="text-orange-700 mt-1">{healthData.nudge_message}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="shadow-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-orange-100 flex items-center justify-center">
                <Utensils className="w-7 h-7 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Uit eten / Fast food</p>
                <p className="font-mono text-3xl font-bold">€{fastFoodSpending.toFixed(2)}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Aandeel van totaal</span>
                <span className="font-semibold">{fastFoodRatio}%</span>
              </div>
              <Progress value={parseFloat(fastFoodRatio)} className="h-3 bg-orange-100" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center">
                <Dumbbell className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Sport & Fitness</p>
                <p className="font-mono text-3xl font-bold">€{sportsSpending.toFixed(2)}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Aandeel van totaal</span>
                <span className="font-semibold">{sportsRatio}%</span>
              </div>
              <Progress value={parseFloat(sportsRatio)} className="h-3 bg-green-100" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="font-heading">Verdeling deze maand</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={120}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `€${value.toFixed(2)}`}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e5e5' }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Insights */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            Gezondheidsbalans Inzichten
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-muted/50">
              <div className="flex items-center gap-3">
                {parseFloat(fastFoodRatio) > 60 ? (
                  <TrendingDown className="w-5 h-5 text-orange-600" />
                ) : (
                  <TrendingUp className="w-5 h-5 text-green-600" />
                )}
                <div>
                  <p className="font-semibold">
                    {parseFloat(fastFoodRatio) > 60 
                      ? 'Een groot deel gaat nu naar eten buiten de deur'
                      : 'Je uitgaven zijn redelijk in balans'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {parseFloat(fastFoodRatio) > 60 
                      ? 'Misschien helpt het om wat vaker thuis te eten als je meer ruimte in je budget wilt houden.'
                      : 'Je verdeelt je uitgaven op dit moment redelijk tussen gemak en beweging.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl border border-border">
                <p className="text-sm text-muted-foreground">Richtbedrag: uit eten</p>
                <p className="font-mono text-lg font-semibold">€100/maand</p>
                <p className={`text-sm mt-1 ${fastFoodSpending > 100 ? 'text-orange-600' : 'text-green-600'}`}>
                  {fastFoodSpending > 100
                    ? `€${(fastFoodSpending - 100).toFixed(2)} boven richtbedrag`
                    : `€${(100 - fastFoodSpending).toFixed(2)} onder richtbedrag`}
                </p>
              </div>
              <div className="p-4 rounded-xl border border-border">
                <p className="text-sm text-muted-foreground">Richtbedrag: sport</p>
                <p className="font-mono text-lg font-semibold">€50/maand</p>
                <p className={`text-sm mt-1 ${sportsSpending > 50 ? 'text-orange-600' : 'text-green-600'}`}>
                  {sportsSpending > 50
                    ? `€${(sportsSpending - 50).toFixed(2)} boven richtbedrag`
                    : `€${(50 - sportsSpending).toFixed(2)} onder richtbedrag`}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
